import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux-toolkit/store';
import { FeaturePermissionMatrix } from '@/apis/types';

/**
 * Custom hook to retrieve permissions for a specific menu item or sub-menu item.
 * Accesses menu data directly from the Redux store and performs case-insensitive matching.
 *
 * @example
 * // Get parent menu permissions:
 * const permissions = useMenuPermissions('Shipment');
 *
 * @example
 * // Get child sub-menu permissions:
 * const permissions = useMenuPermissions('Settings', 'GxP reports');
 *
 * @param parentName - Label of the parent menu item
 * @param childName - Optional label of the child sub-menu item
 * @returns FeaturePermissionMatrix or null if not found
 */
export const useMenuPermissions = (
  parentName = '',
  childName = ''
): FeaturePermissionMatrix | null => {
  const menu = useSelector((state: RootState) => state.menu?.items);

  return useMemo(() => {
    if (!menu?.length || !parentName?.trim()) return null;

    const normalizedParent = parentName.trim().toLowerCase();
    const parentItem = menu.find(
      (item) => item.label?.trim()?.toLowerCase() === normalizedParent
    );

    if (!parentItem) return null;

    if (childName?.trim()) {
      const normalizedChild = childName.trim().toLowerCase();
      const subItem = parentItem.sub_menus?.find(
        (sub) => sub.label?.trim()?.toLowerCase() === normalizedChild
      );

      return subItem?.permissions || null;
    }

    return parentItem.permissions || null;
  }, [menu, parentName, childName]);
};

export default useMenuPermissions;
